import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import ProgressRing from "@/components/ProgressRing";
import ModuleCard from "@/components/ModuleCard";
import { ProjectInquiryCard } from "@/components/ProjectInquiry";
import LicenseUpsell from "@/components/LicenseUpsell";
import { SavedLessons } from "@/components/SavedLessons";
import { BookOpen, Trophy, CheckCircle2, MessageSquareText, ArrowRight, Flame, WifiOff, RotateCw, Award, Lightbulb } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const courseQ = useQuery({ queryKey: ["course"], queryFn: async () => (await api.get("/course")).data });
  const progQ = useQuery({ queryKey: ["progress"], queryFn: async () => (await api.get("/progress")).data });
  const metaQ = useQuery({ queryKey: ["lesson-meta"], queryFn: async () => (await api.get("/lesson-meta")).data });

  const modules = courseQ.data?.modules || [];
  const prog = progQ.data;
  const mp = prog?.modules || {};
  const isTrial = user?.role === "trial";

  // next recommended module: first not complete (Trial nur Gratis-Module)
  const nextModule = isTrial
    ? modules.find((m) => m.free && !mp[m.id]?.complete) || modules.find((m) => m.free) || modules[0]
    : modules.find((m) => !mp[m.id]?.complete) || modules[0];

  // zuletzt gesehene Lektion (Weiterlernen-Sprungmarke)
  const lessonIndex = {};
  modules.forEach((m) => (m.lessons || []).forEach((l) => { lessonIndex[l.id] = { moduleId: m.id, moduleNumber: m.number, lessonTitle: l.title }; }));
  const lastViewed = prog?.last_viewed;
  const lvInfo = lastViewed && lessonIndex[lastViewed.lesson_id];

  const stats = [
    { icon: BookOpen, label: "Lektionen", value: `${prog?.lessons_done ?? 0}/${prog?.total_lessons ?? "–"}` },
    { icon: Trophy, label: "Quiz bestanden", value: `${prog?.quizzes_passed ?? 0}/${prog?.modules_total ?? "–"}` },
    { icon: CheckCircle2, label: "Module fertig", value: `${prog?.modules_complete ?? 0}/${prog?.modules_total ?? "–"}` },
  ];

  return (
    <div className="min-h-screen bg-background grain">
      <Navbar />
      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8 sm:py-10">
        {/* Header / overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="rounded-3xl glass-strong radial-fade p-6 sm:p-10 mb-8"
        >
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-8 justify-between">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold/10 border border-gold/25 text-[11px] uppercase tracking-[0.16em] text-gold mb-4" data-testid="academy-badge">
                <Lightbulb className="h-3 w-3" /> LUX Lighting Academy · Lichtplanungskurs für Architekten
              </div>
              {isTrial && (
                <div className="inline-flex items-center gap-2 px-3 py-1 ml-0 sm:ml-2 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-[11px] uppercase tracking-[0.16em] text-emerald-300 mb-4" data-testid="trial-badge">
                  Gratis-Test · Modul 1 frei
                </div>
              )}
              <p className="text-sm text-gold uppercase tracking-widest">Willkommen, {user?.name?.split(" ")[0] || "Architekt:in"}</p>
              <h1 className="font-heading font-extrabold text-3xl sm:text-4xl mt-2">Ihre Lichtplanungs-Reise</h1>
              <p className="text-foreground/70 mt-3 max-w-xl">
                Arbeiten Sie sich durch 10 fundierte Module – mit interaktiven Erklärungen, Quiz und Ihrem persönlichen KI-Coach.
              </p>
              <div className="flex flex-wrap gap-3 mt-6">
                {lvInfo ? (
                  <Link
                    to={`/module/${lastViewed.module_id}/lesson/${lastViewed.lesson_id}`}
                    data-testid="continue-button"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gold text-[#0a0d12] font-semibold hover:shadow-[0_8px_30px_-8px_rgba(212,175,55,0.7)] transition-all duration-300"
                  >
                    <span className="truncate max-w-[200px] sm:max-w-xs">Weiterlernen: {lvInfo.lessonTitle}</span>
                    <ArrowRight className="h-4 w-4 flex-shrink-0" />
                  </Link>
                ) : nextModule ? (
                  <Link
                    to={`/module/${nextModule.id}`}
                    data-testid="continue-button"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gold text-[#0a0d12] font-semibold hover:shadow-[0_8px_30px_-8px_rgba(212,175,55,0.7)] transition-all duration-300"
                  >
                    {prog?.lessons_done ? "Weiterlernen" : "Jetzt starten"}: Modul {nextModule.number}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                ) : null}
                <Link to="/coach" className="inline-flex items-center gap-2 px-6 py-3 rounded-full glass hover:glow-gold transition-all duration-300">
                  <MessageSquareText className="h-4 w-4 text-gold" /> KI-Coach fragen
                </Link>
                {prog?.modules_total > 0 && prog?.modules_complete >= prog?.modules_total && (
                  <Link
                    to="/zertifikat"
                    data-testid="certificate-cta"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gold/15 border border-gold/40 text-gold hover:glow-gold transition-all duration-300"
                  >
                    <Award className="h-4 w-4" /> Zertifikat ansehen
                  </Link>
                )}
              </div>
            </div>
            <div className="flex flex-col items-center gap-3">
              <ProgressRing percent={prog?.overall_percent ?? 0} size={140} stroke={10} label="Gesamt" />
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Flame className="h-3.5 w-3.5 text-amber-glow" /> Gesamtfortschritt
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mt-8 pt-8 border-t hairline">
            {stats.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="flex items-center gap-3" data-testid={`stat-${i}`}>
                  <div className="h-10 w-10 rounded-xl bg-gold/15 border border-gold/25 flex items-center justify-center flex-shrink-0">
                    <Icon className="h-5 w-5 text-gold" />
                  </div>
                  <div>
                    <div className="font-heading font-bold text-xl">{s.value}</div>
                    <div className="text-xs text-muted-foreground uppercase tracking-wide">{s.label}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Modules */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading font-bold text-2xl">Module</h2>
          {!courseQ.isError && <span className="text-sm text-muted-foreground">{modules.length} Module</span>}
        </div>

        {courseQ.isLoading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-72 rounded-2xl glass animate-pulse" />
            ))}
          </div>
        ) : courseQ.isError ? (
          <div
            data-testid="course-error-state"
            className="rounded-2xl glass border border-white/10 px-6 py-12 flex flex-col items-center text-center"
          >
            <div className="h-14 w-14 rounded-2xl bg-gold/10 border border-gold/25 flex items-center justify-center mb-4">
              <WifiOff className="h-7 w-7 text-gold" />
            </div>
            <h3 className="font-heading font-bold text-xl">Keine Verbindung</h3>
            <p className="text-sm text-muted-foreground mt-2 max-w-md">
              Die Module konnten nicht geladen werden. Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.
            </p>
            <button
              data-testid="course-retry-button"
              onClick={() => {
                courseQ.refetch();
                progQ.refetch();
              }}
              disabled={courseQ.isFetching}
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gold text-[#0a0d12] font-semibold hover:shadow-[0_8px_30px_-8px_rgba(212,175,55,0.7)] transition-all duration-300 disabled:opacity-60"
            >
              <RotateCw className={`h-4 w-4 ${courseQ.isFetching ? "animate-spin" : ""}`} />
              {courseQ.isFetching ? "Wird geladen…" : "Erneut versuchen"}
            </button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {modules.map((m, i) => (
              <ModuleCard key={m.id} module={m} prog={mp[m.id]} index={i} locked={isTrial && !m.free} />
            ))}
          </div>
        )}

        {!courseQ.isError && isTrial && (
          <div className="mt-10">
            <LicenseUpsell />
          </div>
        )}

        {!courseQ.isError && <SavedLessons modules={modules} meta={metaQ.data?.meta} />}

        {!courseQ.isError && !isTrial && <ProjectInquiryCard />}
      </main>
    </div>
  );
}
